import { useEffect, useState } from "react";
import { api } from "@/lib/api";

// Fallback config STABILE (riferimento condiviso a livello di modulo).
// Era ricreato ad ogni render di useMapsConfig → causa principale del loop
// "Maximum update depth exceeded" in OrderTrackingMap (vedi useGoogleMaps).
const DEFAULT_DISABLED_CONFIG = Object.freeze({
  enabled: false,
  provider: null,
  service_area_mode: "ticino",
});

let cachedConfig = null;
let scriptLoadPromise = null;
let configFetchPromise = null;
let authenticationError = null;
const authenticationListeners = new Set();
let rejectPendingLoad = null;

// Google can reject a referrer AFTER the script's ready callback. Notify every
// mounted consumer, including both address fields, instead of leaving disabled
// Google widgets on the page or reporting a working map.
function installAuthenticationHandler() {
  if (window.gm_authFailure?.dapnowHandler) return;
  const previous = window.gm_authFailure;
  const handler = () => {
    authenticationError = new Error("google_maps_auth_failed");
    rejectPendingLoad?.(authenticationError);
    authenticationListeners.forEach(listener => listener(authenticationError));
    if (typeof previous === "function") {
      try { previous(); } catch { /* A third-party handler must not block recovery. */ }
    }
  };
  handler.dapnowHandler = true;
  window.gm_authFailure = handler;
}

/**
 * useMapsConfig — fetch backend maps config (service area, enabled flag).
 * Cached process-wide. Restituisce SEMPRE un riferimento stabile per il
 * fallback in modo che i consumer (useGoogleMaps) non rifirino useEffect
 * su ogni render finché la chiamata backend non risponde.
 */
export function useMapsConfig() {
  const [config, setConfig] = useState(cachedConfig);

  useEffect(() => {
    if (cachedConfig) {
      // Già risolto in un'altra istanza del hook → allinea lo state locale
      // se ancora nullo, senza re-fetch.
      if (!config) setConfig(cachedConfig);
      return;
    }
    // Dedup tra istanze concorrenti dello stesso fetch
    if (!configFetchPromise) {
      configFetchPromise = api.get("/maps/config")
        .then(({ data }) => {
          cachedConfig = data;
          return data;
        })
        .catch(() => {
          cachedConfig = DEFAULT_DISABLED_CONFIG;
          return DEFAULT_DISABLED_CONFIG;
        });
    }
    let cancelled = false;
    configFetchPromise.then((data) => {
      if (!cancelled) setConfig(data);
    });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return config || DEFAULT_DISABLED_CONFIG;
}

export function getBrowserMapsKey() {
  return (process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "").trim();
}

/**
 * isMapsAvailable — both backend has key AND frontend has REACT_APP_GOOGLE_MAPS_API_KEY.
 */
export function isMapsAvailable(config) {
  return Boolean((config?.enabled || cachedConfig?.enabled) && getBrowserMapsKey());
}

/**
 * loadGoogleMapsScript — single-load pattern. Returns a promise that resolves with window.google
 * once the script is ready. Loads Places + Geometry libraries.
 */
export function loadGoogleMapsScript() {
  if (typeof window === "undefined") return Promise.reject(new Error("no window"));
  if (authenticationError) return Promise.reject(authenticationError);
  installAuthenticationHandler();
  if (window.google?.maps) return Promise.resolve(window.google);
  if (scriptLoadPromise) return scriptLoadPromise;
  const apiKey = getBrowserMapsKey();
  if (!apiKey) return Promise.reject(new Error("REACT_APP_GOOGLE_MAPS_API_KEY missing"));

  scriptLoadPromise = new Promise((resolve, reject) => {
    const script = document.createElement("script");
    const cb = `__dapnow_gmaps_cb_${Date.now()}`;
    let settled = false;
    let timeout;
    const finish = (error) => {
      if (settled) return;
      settled = true;
      clearTimeout(timeout);
      delete window[cb];
      rejectPendingLoad = null;
      if (error) reject(error);
      else resolve(window.google);
    };
    rejectPendingLoad = finish;
    window[cb] = () => {
      finish(authenticationError || (!window.google?.maps ? new Error("google_maps_unavailable") : null));
    };
    script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}&libraries=places,geometry&language=it&region=CH&loading=async&callback=${cb}`;
    script.async = true;
    script.defer = true;
    script.onerror = () => {
      finish(new Error("google_maps_load_failed"));
    };
    timeout = setTimeout(() => finish(new Error("google_maps_load_timeout")), 12000);
    document.head.appendChild(script);
  });
  return scriptLoadPromise;
}

/**
 * useGoogleMaps — hook returning {ready, error, google, mapsKey, config}.
 * If maps is not available (no key on backend or browser) returns ready=false, error="disabled".
 *
 * Phase 44.11 — Fix loop "Maximum update depth exceeded":
 *   1. Dipendenza sull'attributo primitivo `enabled` invece dell'oggetto
 *      `config` (che cambiava reference ad ogni render del fallback).
 *   2. setState protetto da equality-check funzionale per evitare re-render
 *      a vuoto quando i valori sono identici.
 */
export function useGoogleMaps() {
  const config = useMapsConfig();
  const enabled = isMapsAvailable(config);
  const [state, setState] = useState({ ready: false, error: null, google: null });

  useEffect(() => {
    if (!enabled) {
      setState((prev) => (
        !prev.ready && prev.error === "disabled" && prev.google === null
          ? prev
          : { ready: false, error: "disabled", google: null }
      ));
      return;
    }
    let cancelled = false;
    const onAuthenticationFailure = (err) => {
      if (!cancelled) setState({ ready: false, error: err.message, google: null });
    };
    authenticationListeners.add(onAuthenticationFailure);
    loadGoogleMapsScript()
      .then((g) => {
        if (cancelled) return;
        if (authenticationError) {
          onAuthenticationFailure(authenticationError);
          return;
        }
        setState((prev) => (
          prev.ready && prev.google === g && prev.error === null
            ? prev
            : { ready: true, error: null, google: g }
        ));
      })
      .catch((err) => {
        if (cancelled) return;
        const msg = err?.message || "load_failed";
        setState((prev) => (
          !prev.ready && prev.error === msg && prev.google === null
            ? prev
            : { ready: false, error: msg, google: null }
        ));
      });
    return () => {
      cancelled = true;
      authenticationListeners.delete(onAuthenticationFailure);
    };
  }, [enabled]);

  return { ...state, mapsKey: getBrowserMapsKey(), config };
}

/**
 * geocodeBackend — server-side geocode (uses cached_geocode + service area bias).
 * Returns {found, lat, lng, formatted_address, place_id, provider}.
 */
export async function geocodeBackend(address) {
  if (!address) return { found: false };
  try {
    const { data } = await api.post("/maps/geocode", { address });
    return data;
  } catch {
    return { found: false };
  }
}
